import { createReference, MedplumClient } from "@medplum/core";
import { DiagnosticReport, List, ServiceRequest } from "@medplum/fhirtypes";

import { formatISO } from "date-fns";

import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";

import { PREGNANCY_RECORD_CODING_QUERY } from "../constants/record";
import { PregnancyRecord } from "../types";

import { buildPregnancyRecord } from "./record";

// --- Update ------------------------------------------------------------------

/**
 * Adds a lab order (ServiceRequest) entry to a pregnancy record `List`.
 *
 * If the ServiceRequest is already referenced in `List.entry`, the call is a
 * no-op and the unchanged resource is returned. Otherwise a new entry is
 * appended and the updated `List` is persisted via `medplum.updateResource`.
 *
 * @param medplum - Medplum client
 * @param pregnancyRecord - The pregnancy record `List` to update; mutated in place
 * @param serviceRequest - FHIR `ServiceRequest` to link; must have a valid `id`
 * @returns The updated (or unchanged) FHIR `List` resource
 */
export async function upsertLabOrderToPregnancyRecord(
  medplum: MedplumClient,
  pregnancyRecord: List,
  serviceRequest: ServiceRequest,
): Promise<List> {
  const serviceRequestReference = createReference(serviceRequest);

  const alreadyLinked = pregnancyRecord.entry?.some(
    (item) => item.item?.reference === serviceRequestReference.reference,
  );

  if (alreadyLinked) return pregnancyRecord;

  pregnancyRecord.entry = [
    ...(pregnancyRecord.entry ?? []),
    {
      item: serviceRequestReference,
      date: serviceRequest.authoredOn ?? formatISO(new Date()),
    },
  ];

  return medplum.updateResource(pregnancyRecord);
}

/**
 * Adds a `DiagnosticReport` entry to a pregnancy record `List`.
 *
 * Used when no `ServiceRequest` is available to link (e.g. externally sourced
 * results). If the report is already referenced the call is a no-op.
 *
 * @param medplum - Medplum client
 * @param pregnancyRecord - The pregnancy record `List` to update; mutated in place
 * @param diagnosticReport - FHIR `DiagnosticReport` to link; must have a valid `id`
 * @returns The updated (or unchanged) FHIR `List` resource
 */
export async function upsertDiagnosticReportToPregnancyRecord(
  medplum: MedplumClient,
  pregnancyRecord: List,
  diagnosticReport: DiagnosticReport,
): Promise<List> {
  const diagnosticReportReference = createReference(diagnosticReport);

  const alreadyLinked = pregnancyRecord.entry?.some(
    (item) => item.item?.reference === diagnosticReportReference.reference,
  );

  if (alreadyLinked) return pregnancyRecord;

  pregnancyRecord.entry = [
    ...(pregnancyRecord.entry ?? []),
    {
      item: diagnosticReportReference,
      date: diagnosticReport.issued ?? formatISO(new Date()),
    },
  ];

  return medplum.updateResource(pregnancyRecord);
}

// --- Fetch -------------------------------------------------------------------

/**
 * Finds the pregnancy record (`List`) whose entries contain the given
 * `ServiceRequest`, enabling GA calculation based on the pregnancy the order
 * was placed under rather than the currently active pregnancy.
 *
 * Returns `undefined` when no pregnancy record references this ServiceRequest
 * (e.g. orders created before Phase 2 was deployed).
 *
 * @param medplum - Medplum client
 * @param patientId - Patient resource ID
 * @param serviceRequestId - ServiceRequest resource ID to look up
 * @returns The matching `PregnancyRecord` or `undefined`
 */
export async function fetchPregnancyRecordForLabOrder(
  medplum: MedplumClient,
  patientId: string,
  serviceRequestId: string,
): Promise<PregnancyRecord | undefined> {
  const { bundle } = await medplum.searchResources("List", {
    subject: joinReferenceString("Patient", patientId),
    code: PREGNANCY_RECORD_CODING_QUERY,
    item: joinReferenceString("ServiceRequest", serviceRequestId),
    _sort: "-_lastUpdated",
  });

  const list = getResourcesFromBundle<List>("List", bundle)?.[0];
  if (!list) return undefined;

  return buildPregnancyRecord(medplum, list);
}

/**
 * Finds the pregnancy record (`List`) whose entries contain the given
 * `DiagnosticReport`. Used as a fallback when no `ServiceRequest` entry exists
 * (e.g. directly-linked DiagnosticReports from manual or uploaded results).
 *
 * @param medplum - Medplum client
 * @param patientId - Patient resource ID
 * @param diagnosticReportId - DiagnosticReport resource ID to look up
 * @returns The matching `PregnancyRecord` or `undefined`
 */
export async function fetchPregnancyRecordForDiagnosticReport(
  medplum: MedplumClient,
  patientId: string,
  diagnosticReportId: string,
): Promise<PregnancyRecord | undefined> {
  const { bundle } = await medplum.searchResources("List", {
    subject: joinReferenceString("Patient", patientId),
    code: PREGNANCY_RECORD_CODING_QUERY,
    item: joinReferenceString("DiagnosticReport", diagnosticReportId),
    _sort: "-_lastUpdated",
  });

  const list = getResourcesFromBundle<List>("List", bundle)?.[0];
  if (!list) return undefined;

  return buildPregnancyRecord(medplum, list);
}
